// config/navGroups.js — SATU sumber pengelompokan menu untuk desktop & mobile.
// ─────────────────────────────────────────────────────────────────────────────
// Sebelum berkas ini ada, pengelompokan ditulis DUA KALI: field `group` di
// ALL_MENUS (Sidebar.js) dan konstanta BOARD_GROUPS (MobileNav.jsx). Keduanya
// sudah menyimpang — lima board (SAP Roles, Logon Audit, SAProuter Log,
// Customizing, AWS Cost Brief) hanya ada di sisi desktop, sehingga di mobile
// terlempar ke tab "More" bersama setelan admin. Penyimpangan seperti itu tidak
// pernah terlihat sebagai galat; ia hanya membuat halaman sulit ditemukan.
//
// Aturannya sekarang: menambah halaman = menambah SATU baris di NAV_GROUP.
// Halaman yang belum terdaftar tetap muncul (jatuh ke FALLBACK_GROUP), bukan
// hilang — halaman yang tidak bisa dicapai adalah kegagalan yang lebih buruk
// daripada halaman yang salah kelompok.
//
// Berkas ini SENGAJA bebas import (tidak ada komponen, tidak ada ikon), sama
// seperti routesMeta.js, supaya bisa dipakai kedua sisi tanpa circular import.

// Urutan tampil grup di sidebar. Grup yang tidak punya menu terlihat otomatis
// dilewati, jadi daftar ini boleh memuat grup yang kosong untuk sebagian user.
export const GROUP_ORDER = [
  'AWS', 'SAP', 'INFRA', 'HR', 'DOCS', 'AI', 'ACCESS', 'SYSTEM', 'INTEGRATION', 'TOOLS',
];

// Label grup → key terjemahan.
export const GROUP_KEY = {
  AWS:         'sidebar.group.aws',
  SAP:         'sidebar.group.sap',
  INFRA:       'sidebar.group.infra',
  DOCS:        'sidebar.group.docs',
  HR:          'sidebar.group.hr',
  AI:          'sidebar.group.ai',
  ACCESS:      'sidebar.group.access',
  SYSTEM:      'sidebar.group.system',
  INTEGRATION: 'sidebar.group.integration',
  TOOLS:       'sidebar.group.tools',
};

// Grup yang dianggap "board" (data operasional) oleh tab Boards di mobile.
// Sisanya — AI, ACCESS, SYSTEM, INTEGRATION, TOOLS — masuk tab "More". Pembagian
// ini mengikuti pertanyaan yang dibawa pengguna: "mau lihat data apa" versus
// "mau mengatur apa".
export const BOARD_GROUPS = ['AWS', 'SAP', 'INFRA', 'HR', 'DOCS'];

// Grup untuk key yang belum didaftarkan. Bukan grup buangan — ia terlihat,
// sehingga halaman baru yang lupa dikelompokkan tetap bisa dibuka.
export const FALLBACK_GROUP = 'TOOLS';

export const NAV_GROUP = {
  // ── AWS ───────────────────────────────────────────────────────────────────
  aws_billing:        'AWS',
  aws_cost:           'AWS',
  aws_cost_brief:     'AWS',
  aws_inventory:      'AWS',
  aws_cloudtrail:     'AWS',
  aws_pricing:        'AWS',
  linux_login_audit: 'AWS',
  aws_ec2_health:     'AWS',
  doc_dms:            'DOCS',
  doc_mr_report:      'DOCS',
  doc_atmosfer:       'DOCS',
  doc_process_map:    'DOCS',
  // ── SAP ───────────────────────────────────────────────────────────────────
  solman:             'SAP',
  solman_docs:        'SAP',
  sap_usage:          'SAP',
  sap_user_mgmt:      'SAP',
  sap_roles:          'SAP',
  sap_basis_mon:      'SAP',
  sap_logon_audit:    'SAP',
  sap_router_log:     'SAP',
  sap_customizing:    'SAP',
  sap_config:         'SAP',
  sap_source:         'SAP',
  transport:          'SAP',
  sap_notes:          'SAP',
  // ── Infra & layanan ───────────────────────────────────────────────────────
  m365:               'INFRA',
  sdp:                'INFRA',
  netmap:                'INFRA',
  pc_health:          'INFRA',
  // ── HR ────────────────────────────────────────────────────────────────────
  employee_overtime:  'HR',
  // ── AI ────────────────────────────────────────────────────────────────────
  ai_workspace:       'AI',
  ai_coding:          'AI',
  ai_actions:         'AI',
  ai_history:         'AI',
  claude_activity:    'AI',
  ai_datasources:     'AI',
  ai_config_settings: 'AI',
  // ── Akses & pengguna ──────────────────────────────────────────────────────
  users:              'ACCESS',
  roles:              'ACCESS',
  sessions:           'ACCESS',
  share_links:        'ACCESS',
  // ── Sistem ────────────────────────────────────────────────────────────────
  // Activity Log ada di sini, bukan di INFRA. Ia mencatat perbuatan pengguna di
  // dashboard ini sendiri — satu kelas dengan Session Log dan File Deploy, bukan
  // dengan M365/SDP yang merupakan sistem pihak ketiga.
  logs:               'SYSTEM',
  table_maintenance:  'SYSTEM',
  file_deploy:        'SYSTEM',
  monthly_report:     'SYSTEM',
  // ── Integrasi keluar ──────────────────────────────────────────────────────
  telegram:           'INTEGRATION',
  telegram_log:       'INTEGRATION',
  // ── Alat ──────────────────────────────────────────────────────────────────
  notes:              'TOOLS',
  notes_plain:        'TOOLS',
};

// Permission yang BUKAN halaman. NAV_GROUP hanya memetakan key halaman, jadi
// izin-kapabilitas seperti 'transport_import' atau 'm365_pricing_edit' akan
// jatuh ke FALLBACK_GROUP kalau tidak didaftarkan di sini — tampil di kelompok
// yang salah pada panel "Dashboard Access". Dipisah dari NAV_GROUP supaya jelas
// bahwa menambah baris di sini TIDAK menambah menu.
export const PERM_GROUP_EXTRA = {
  transport_import:  'SAP',   // tombol import di layar Transport
  sap_table_peek:    'SAP',   // panel sampel isi tabel di ABAP Source Viewer
  role_management:   'ACCESS',
  ai_config:         'AI',
  ai_settings:       'AI',
  m365_license:      'INFRA',
  m365_pricing_edit: 'INFRA',
};

export function groupOf(key) {
  return NAV_GROUP[key] || FALLBACK_GROUP;
}

// Kelompok untuk sebuah permission: cari di peta halaman dulu, lalu di peta
// izin-kapabilitas. Urutan ini penting agar key yang ada di dua tempat selalu
// mengikuti kelompok menunya.
export function groupOfPermission(perm) {
  return NAV_GROUP[perm] || PERM_GROUP_EXTRA[perm] || FALLBACK_GROUP;
}

export function isBoardKey(key) {
  return BOARD_GROUPS.includes(groupOf(key));
}
